/**
 * PDFViewer - Web Implementation
 *
 * Uses react-pdf (PDF.js) to render PDFs in the browser.
 * Supports page navigation and zoom.
 *
 * Note: react-pdf renders HTML elements, so we use native HTML containers
 * instead of React Native View/ScrollView components.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useWindowDimensions } from 'react-native';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFViewerProps } from '../../../types/pdf';

// Import react-pdf styles for text and annotation layers
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure PDF.js worker
// Using CDN for the worker to avoid bundling issues
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const PDFViewer: React.FC<PDFViewerProps> = ({
  source,
  page,
  scale,
  onPageChange,
  onLoadComplete,
  onError,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const { width: windowWidth } = useWindowDimensions();

  // Calculate page width based on window size and scale
  // Leave some padding on the sides
  const maxPageWidth = Math.min(windowWidth - 40, 800);
  const pageWidth = maxPageWidth * scale;

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: loadedNumPages }: { numPages: number }) => {
      setNumPages(loadedNumPages);
      onLoadComplete(loadedNumPages);
    },
    [onLoadComplete]
  );

  const onDocumentLoadError = useCallback(
    (error: Error) => {
      onError(error);
    },
    [onError]
  );

  // Notify parent when page prop changes are out of bounds
  useEffect(() => {
    if (numPages > 0 && page > numPages) {
      onPageChange(numPages);
    }
  }, [page, numPages, onPageChange]);

  // Use native HTML elements since react-pdf renders HTML
  return (
    <div style={containerStyle}>
      <div style={documentWrapperStyle}>
        <Document
          file={source}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          error={null}
        >
          <Page
            pageNumber={page}
            width={pageWidth}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            loading={null}
          />
        </Document>
      </div>
    </div>
  );
};

// Use plain CSS objects for web-native elements
const containerStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: '#f5f5f5',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px 0',
  minHeight: '100%',
};

const documentWrapperStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
};
